import type { CSSProperties } from 'react';
import { ModalLocation } from '@/constants/componentPresets';
import { pxToRem } from '@/theme/spacing';
import { zIndex } from '@/theme/zIndex';
import {
  CMODAL_BACKDROP_TOP_OFFSET,
  CMODAL_CENTER_BACKDROP_ANIMATION_MS,
  CMODAL_CENTER_BACKDROP_BLUR,
  CMODAL_CENTER_BACKDROP_OPACITY,
  CMODAL_CONTENT_GAP,
  CMODAL_MOBILE_SIDE_PADDING,
  CMODAL_SIDE_BACKDROP_BLUR,
  CMODAL_SIDE_BACKDROP_OPACITY,
  CMODAL_SIDE_EDGE_MARGIN,
  CMODAL_SIDE_TOP_MARGIN,
  CMODAL_TITLE_GAP,
  CSS_AUTO,
  CSS_FLEX_NONE,
  CSS_FULL_SIZE,
  MODAL_CENTER_WIDTH,
  MODAL_MIN_HEIGHT,
  MODAL_SIDE_WIDTH,
  NAVBAR_HEIGHT,
} from './Cmodal.constants';

interface ModalLocationInput {
  location: ModalLocation;
}

interface ModalContentWrapperStyleInput extends ModalLocationInput {
  isMobile: boolean;
}

interface CmodalBackdropSettingsInput extends ModalLocationInput {
  animationDuration: number;
}

interface CmodalBackdropSettings {
  topOffset: number;
  opacity: number;
  blur: number;
  useGradient: boolean;
  animationDuration: number;
}

interface CmodalTitleWrapperStyleInput {
  hasRightComponent: boolean;
}

const NAVBAR_TOP_OFFSET = pxToRem(NAVBAR_HEIGHT);
const MODAL_CENTER_WIDTH_REM = pxToRem(MODAL_CENTER_WIDTH);
const MODAL_SIDE_WIDTH_REM = pxToRem(MODAL_SIDE_WIDTH);
const MODAL_MIN_HEIGHT_REM = pxToRem(MODAL_MIN_HEIGHT);
const MODAL_MOBILE_SIDE_PADDING_REM = pxToRem(CMODAL_MOBILE_SIDE_PADDING);
const MODAL_SIDE_EDGE_MARGIN_REM = pxToRem(CMODAL_SIDE_EDGE_MARGIN);
const MODAL_SIDE_TOP_MARGIN_REM = pxToRem(CMODAL_SIDE_TOP_MARGIN);
const MODAL_CONTENT_GAP_REM = pxToRem(CMODAL_CONTENT_GAP);
const MODAL_TITLE_GAP_REM = pxToRem(CMODAL_TITLE_GAP);

const MODAL_CONTAINER_TOP_BY_LOCATION: Record<ModalLocation, CSSProperties['top']> = {
  [ModalLocation.Left]: NAVBAR_TOP_OFFSET,
  [ModalLocation.Right]: NAVBAR_TOP_OFFSET,
  [ModalLocation.Center]: 0,
};

const MODAL_CONTAINER_ALIGN_ITEMS_BY_LOCATION: Record<ModalLocation, CSSProperties['alignItems']> = {
  [ModalLocation.Left]: 'flex-start',
  [ModalLocation.Right]: 'flex-start',
  [ModalLocation.Center]: 'center',
};

const MODAL_CONTAINER_JUSTIFY_CONTENT_BY_LOCATION: Record<ModalLocation, CSSProperties['justifyContent']> = {
  [ModalLocation.Left]: 'flex-start',
  [ModalLocation.Right]: 'flex-end',
  [ModalLocation.Center]: 'center',
};

const MODAL_CONTAINER_EXTRA_STYLE_BY_LOCATION: Record<ModalLocation, CSSProperties> = {
  [ModalLocation.Left]: {},
  [ModalLocation.Right]: {},
  [ModalLocation.Center]: {
    height: '100%',
    overflow: 'auto',
  },
};

const MODAL_CONTENT_WRAPPER_HEIGHT_BY_LOCATION: Record<ModalLocation, CSSProperties['height']> = {
  [ModalLocation.Left]: 'calc(100% - 30px)',
  [ModalLocation.Right]: 'calc(100% - 30px)',
  [ModalLocation.Center]: CSS_AUTO,
};

function getModalContentWrapperBaseStyle({ location }: ModalLocationInput): CSSProperties {
  return {
    position: 'relative',
    height: MODAL_CONTENT_WRAPPER_HEIGHT_BY_LOCATION[location],
  };
}

function getCenterModalContentWrapperStyle({
  baseStyle,
  isMobile,
}: {
  baseStyle: CSSProperties;
  isMobile: boolean;
}): CSSProperties {
  return {
    ...baseStyle,
    width: isMobile ? '90%' : MODAL_CENTER_WIDTH_REM,
    maxWidth: MODAL_CENTER_WIDTH_REM,
    flex: '1 1 auto',
  };
}

function getMobileSideModalContentWrapperStyle({
  baseStyle,
}: {
  baseStyle: CSSProperties;
}): CSSProperties {
  return {
    ...baseStyle,
    width: '100%',
    paddingLeft: MODAL_MOBILE_SIDE_PADDING_REM,
    paddingRight: MODAL_MOBILE_SIDE_PADDING_REM,
  };
}

function getDesktopSideModalContentWrapperStyle({
  baseStyle,
  location,
}: {
  baseStyle: CSSProperties;
  location: ModalLocation;
}): CSSProperties {
  return {
    ...baseStyle,
    width: MODAL_SIDE_WIDTH_REM,
    marginLeft: location === ModalLocation.Left ? MODAL_SIDE_EDGE_MARGIN_REM : 0,
    marginRight: location === ModalLocation.Right ? MODAL_SIDE_EDGE_MARGIN_REM : 0,
    marginTop: MODAL_SIDE_TOP_MARGIN_REM,
  };
}

export function getModalContainerStyle({ location }: ModalLocationInput): CSSProperties {
  return {
    position: 'fixed',
    top: MODAL_CONTAINER_TOP_BY_LOCATION[location],
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: zIndex.modalContainer,
    display: 'flex',
    alignItems: MODAL_CONTAINER_ALIGN_ITEMS_BY_LOCATION[location],
    justifyContent: MODAL_CONTAINER_JUSTIFY_CONTENT_BY_LOCATION[location],
    pointerEvents: 'none',
    ...MODAL_CONTAINER_EXTRA_STYLE_BY_LOCATION[location],
  };
}

export function getModalTileWrapperStyle({ location }: ModalLocationInput): CSSProperties {
  return {
    position: 'relative',
    pointerEvents: 'auto',
    width: '100%',
    overflow: 'visible',
    height: location === ModalLocation.Center ? CSS_AUTO : CSS_FULL_SIZE,
    minHeight: location === ModalLocation.Center ? CSS_AUTO : CSS_FULL_SIZE,
  };
}

export function getModalTileStyle({ location }: ModalLocationInput): CSSProperties {
  return {
    position: 'relative',
    width: '100%',
    height: location === ModalLocation.Center ? CSS_AUTO : CSS_FULL_SIZE,
    minHeight: location === ModalLocation.Center ? MODAL_MIN_HEIGHT_REM : '100%',
    boxShadow: 'rgba(0, 0, 0, 0.21) 2px 1px 15px 0px, rgba(255, 255, 255, 0.15) 1px 1px 2px 1px inset, rgba(255, 255, 255, 0.03) -1px -2px 0px 0px inset, rgba(0, 0, 0, 0.11) -1px -1px 2px 0px inset, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset',
    ...(location === ModalLocation.Center && { backgroundColor: 'rgba(255, 255, 255, 0.045)' }),
  };
}

export function getModalContentWrapperStyle({
  location,
  isMobile,
}: ModalContentWrapperStyleInput): CSSProperties {
  const baseStyle = getModalContentWrapperBaseStyle({ location });

  if (location === ModalLocation.Center) {
    return getCenterModalContentWrapperStyle({ baseStyle, isMobile });
  }

  if (isMobile) {
    return getMobileSideModalContentWrapperStyle({ baseStyle });
  }

  return getDesktopSideModalContentWrapperStyle({ baseStyle, location });
}

export function getBackButtonStyle(): CSSProperties {
  return {
    position: 'absolute',
    top: '2rem',
    right: '2rem',
    zIndex: zIndex.modalBackButton,
  };
}

export function getCmodalBackdropSettings({
  location,
  animationDuration,
}: CmodalBackdropSettingsInput): CmodalBackdropSettings {
  const isCenterModal = location === ModalLocation.Center;

  return {
    topOffset: CMODAL_BACKDROP_TOP_OFFSET,
    opacity: isCenterModal ? CMODAL_CENTER_BACKDROP_OPACITY : CMODAL_SIDE_BACKDROP_OPACITY,
    blur: isCenterModal ? CMODAL_CENTER_BACKDROP_BLUR : CMODAL_SIDE_BACKDROP_BLUR,
    useGradient: isCenterModal,
    animationDuration: isCenterModal ? CMODAL_CENTER_BACKDROP_ANIMATION_MS : animationDuration,
  };
}

export function getContentStyle({ location }: ModalLocationInput): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: MODAL_CONTENT_GAP_REM,
    width: '100%',
    height: location === ModalLocation.Center ? 'auto' : '100%',
  };
}

export function getTitleWrapperStyle({
  hasRightComponent,
}: CmodalTitleWrapperStyleInput): CSSProperties {
  return {
    paddingLeft: '1rem',
    paddingTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: hasRightComponent ? 'space-between' : 'flex-start',
    width: '100%',
  };
}

export function getTitleLeftStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: MODAL_TITLE_GAP_REM,
  };
}

export function getTitleRightStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
  };
}

export function getMainContentStyle({ location }: ModalLocationInput): CSSProperties {
  return {
    flex: location === ModalLocation.Center ? CSS_FLEX_NONE : 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    paddingTop: '2px',
  };
}
